import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { toast } from 'sonner';
import { useAccount as useWagmiAccount, useSignMessage, useChainId, useSwitchChain } from 'wagmi';
// Component
import WalletConnect from '~/components/wallet-connect';
import { authAPIService } from '~/services/api/auth.service';
import SignatureLayout from '~/components/signature-layout';
import { Button } from '@gu-corp/gu-ui';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '~/store/configure-store';
import { getAccountAction } from '~/store/actions/user-state-actions';
import Loading from '~/components/loading';
import { useTranslation } from 'next-i18next';
import { Loader2 } from 'lucide-react';
import { useLogout } from './use-logout';
import { useRouter } from 'next/router';
import { AppRoutesEnum } from '~/enums/route.enum';
import * as Sentry from '@sentry/nextjs';
import { getEnv } from '~/env';

const AccountContext = createContext({
  account: {} as Required<RootState['userState']>['account'],
});

export const useAccount = () => useContext(AccountContext);

export const withAccount = <P extends Record<string, unknown>>(
  ChildComponent: React.ComponentType<P>,
) => {
  const WithEthereumAddressHOC = (props: P) => {
    const { address: ethereumAddress, status } = useWagmiAccount();

    // E2E test bypass: skip wallet check when test cookie is present
    // SECURITY: Only works in non-production environments
    const [isE2EBypass, setIsE2EBypass] = useState(false);
    useEffect(() => {
      if (
        typeof window !== 'undefined' &&
        document.cookie.includes('e2e_bypass=true') &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      ) {
        setIsE2EBypass(true);
      }
    }, []);

    // Mock account for E2E bypass
    const e2eMockAccount = (() => {
      if (!isE2EBypass) return null;
      const balanceCookie =
        typeof window !== 'undefined'
          ? document.cookie.split(';').find((c) => c.trim().startsWith('e2e_balance='))
          : null;
      const balanceJoc = balanceCookie ? balanceCookie.split('=')[1].trim() : '5000';
      const addressCookie =
        typeof window !== 'undefined'
          ? document.cookie.split(';').find((c) => c.trim().startsWith('e2e_address='))
          : null;
      return {
        ethAddress: addressCookie
          ? addressCookie.split('=')[1].trim().toLowerCase()
          : '0xf39fd6e51aad88f6f4ce6ab8827279cfffb92266',
        balance: BigInt(balanceJoc) * BigInt(10 ** 18),
      } as any;
    })();
    const { t } = useTranslation();
    const { signMessageAsync } = useSignMessage();
    const chainId = useChainId();
    const { switchChainAsync } = useSwitchChain();
    const dispatch = useDispatch();
    const account = useSelector((state: RootState) => state.userState?.account);
    const [loadingInit, setLoadingInit] = useState(() => !account);
    const [loginStatus, setLoginStatus] = useState<
      'get_siwe_message_requesting' | 'signature_requesting' | 'verifying_signature' | 'error'
    >();
    const [siweMessage, setSiweMessage] = useState<string>('');
    const signingInProgressRef = useRef(false);
    const logout = useLogout();
    const router = useRouter();

    // Expected chain ID from environment
    const expectedChainId = parseInt(getEnv('NEXT_PUBLIC_JOC_CHAIN_ID') || '81', 10);

    const onInit = useCallback(async () => {
      if (status === 'connected') {
        if (!account) {
          try {
            await dispatch(getAccountAction());
          } catch (error: any) {
            // do nothing
          }
        }
        setLoadingInit(false);
      } else if (status === 'disconnected') {
        setLoadingInit(false);
      }
    }, [account, dispatch, status]);

    useEffect(() => {
      onInit();
    }, [onInit]);

    const onSiweLogin = useCallback(
      async (walletAddress: string) => {
        // Prevent multiple simultaneous login attempts
        if (signingInProgressRef.current) {
          return;
        }
        signingInProgressRef.current = true;

        try {
          // Step 0: Switch to correct chain if needed
          if (chainId !== expectedChainId) {
            setLoginStatus('get_siwe_message_requesting');
            try {
              await switchChainAsync({ chainId: expectedChainId });
              // Wait a bit for the chain switch to propagate
              await new Promise((resolve) => setTimeout(resolve, 500));
            } catch (switchError: any) {
              console.error('Chain switch failed:', switchError.message);
              toast.error(t('please_switch_to_joc_chain'));
              setLoginStatus('error');
              signingInProgressRef.current = false;
              return;
            }
          }

          // Step 1: Get SIWE message from server
          setLoginStatus('get_siwe_message_requesting');
          const siweChainId = parseInt(getEnv('NEXT_PUBLIC_JOC_CHAIN_ID') || '81', 10);
          const {
            data: { message },
          } = await authAPIService.getSiweMessage(walletAddress, siweChainId);
          setSiweMessage(message);

          // Step 2: Automatically request signature (no extra button click needed)
          setLoginStatus('signature_requesting');
          const signature = await signMessageAsync({ message });
          console.log('Signature result:', signature, typeof signature);

          if (!signature) {
            throw new Error('Signature is empty or undefined');
          }

          // Step 3: Verify signature on server
          setLoginStatus('verifying_signature');
          console.log('Sending to verify:', {
            messageLength: message.length,
            signature: signature.substring(0, 30) + '...',
          });
          await authAPIService.verifySiweSignature(message, signature);
          await dispatch(getAccountAction());

          // Success - clear state and redirect to dashboard
          setLoginStatus(undefined);
          setSiweMessage('');
          if (router.pathname !== AppRoutesEnum.DASHBOARD) {
            router.push(AppRoutesEnum.DASHBOARD, AppRoutesEnum.DASHBOARD, {
              locale: router.locale,
            });
          }
        } catch (error: any) {
          console.error('SIWE Login Error:', error);
          console.error('Error response:', error.response?.data);
          Sentry.captureException(error);
          // Check if user rejected the signature
          if (
            error.code === 4001 ||
            error.message?.includes('rejected') ||
            error.message?.includes('denied')
          ) {
            toast.warning(t('signature_rejected'));
          } else {
            toast.error(error.message);
          }
          setLoginStatus('error');
          setSiweMessage('');
        } finally {
          signingInProgressRef.current = false;
        }
      },
      [signMessageAsync, dispatch, router, t, chainId, expectedChainId, switchChainAsync],
    );

    // Auto-login: trigger SIWE signature as soon as wallet connects
    useEffect(() => {
      if (
        !loadingInit &&
        ethereumAddress &&
        account?.ethAddress !== ethereumAddress.toLowerCase() &&
        !signingInProgressRef.current &&
        !loginStatus // Don't trigger if already in progress or error
      ) {
        onSiweLogin(ethereumAddress);
      }
    }, [ethereumAddress, onSiweLogin, account, loadingInit, loginStatus]);

    if (!isE2EBypass && (status === 'reconnecting' || loadingInit)) {
      return <Loading />;
    }

    if (!isE2EBypass && !ethereumAddress) {
      return <WalletConnect />;
    }

    // Show signature layout if: no account, address mismatch, or login in progress
    const needsSignature =
      !isE2EBypass && (!account || account.ethAddress !== ethereumAddress?.toLowerCase());
    const isLoading = loginStatus && loginStatus !== 'error';

    if (needsSignature || isLoading) {
      return (
        <SignatureLayout
          title={t('sign_in_with_wallet')}
          description={t('signature_required_description')}
        >
          <Button
            className="w-full"
            size="lg"
            data-testid="resign-button"
            onClick={() => ethereumAddress && onSiweLogin(ethereumAddress)}
            disabled={isLoading || !ethereumAddress}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isLoading
              ? loginStatus === 'get_siwe_message_requesting'
                ? t('preparing_sign_in')
                : loginStatus === 'signature_requesting'
                ? t('signature_requesting')
                : t('verifying_signature')
              : t('sign')}
          </Button>
          <Button
            className="w-full"
            variant="outline"
            size="lg"
            data-testid="cancel-resign-button"
            onClick={logout}
            disabled={isLoading}
          >
            {t('cancel')}
          </Button>
        </SignatureLayout>
      );
    }

    return (
      <AccountContext.Provider
        value={{
          account: isE2EBypass ? account || e2eMockAccount : account,
        }}
      >
        <ChildComponent {...props} />
      </AccountContext.Provider>
    );
  };

  WithEthereumAddressHOC.displayName = 'WithEthereumAddressHOC';
  hoistNonReactStatics(WithEthereumAddressHOC, ChildComponent);

  return WithEthereumAddressHOC;
};
